//Chat component for the application, handles real-time communication using WebRTC and Socket.IO
// Primary Author: Justin Andrew

//NOTE UNCOMMENT THIS LINE FOR HANDLE FEATURE
/*inCall && users.length > 1
                                ? remoteUserProfile.userHandle || "@Undefined"
                                : !inCall 
                                    ? "@[Candidate]" 
                                    : "@Processing..."*/

import React, { useState, useEffect, useRef } from "react";
import { useNavigate } from "react-router-dom"; // Add this import
import {
  Container,
  Box,
  Paper,
  Button,
  Typography,
  IconButton,
  Avatar,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  FormControlLabel,
  Switch,
  Divider,
  TextField,
  useMediaQuery,
} from "@mui/material";
import ChatBubbleIcon from "@mui/icons-material/ChatBubble";
import CallIcon from "@mui/icons-material/Call";
import CallEndIcon from "@mui/icons-material/CallEnd";
import io from "socket.io-client";
import type { Socket } from "socket.io-client";
import SessionFilter, {
  FilterCriteria,
  getDefaultFilterCriteria,
} from "./SessionFilter";
import PersonIcon from "@mui/icons-material/Person";
import UserProfileSettings from "../Profile/Profile";
import { useUserProfile } from "../Profile/UserProfileContext";
import { getAuth } from "firebase/auth";
import { validateProfile } from "../Profile/Profile";
import { useCallStatus } from "../Context/CallContext";
import VolumeControl from "./VolumeControl";

//Elevation standard for front end components
const elevationNum = 0;

const audioContext = new (window.AudioContext ||
  (window as any).webkitAudioContext)();

interface User {
  id: string;
  name: string;
  profile?: {
    userName?: string;
    userHandle?: string;
    ethnicity?: string;
    religion?: string;
    location?: string;
    politicalView?: string;
    topic?: string;
    verified?: boolean;
    veteran?: boolean;
  };
}
interface Message {
  id: string;
  name: string;
  message: string;
  sentByMe?: boolean;
}

interface AudioVisualizerProps {
  audioRef: React.RefObject<HTMLAudioElement>;
  inCall: boolean; // Add this prop
}

const AudioVisualizer = ({ audioRef, inCall }: AudioVisualizerProps) => {
  const [audioLevel, setAudioLevel] = useState<number>(0);
  const animationRef = useRef<number | null>(null);
  const analyserRef = useRef<AnalyserNode | null>(null);
  const dataArrayRef = useRef<Uint8Array | null>(null);

  useEffect(() => {
    if (!inCall) {
      setAudioLevel(0);

      // Clean up any ongoing audio analysis
      if (animationRef.current) {
        cancelAnimationFrame(animationRef.current);
        animationRef.current = null;
      }

      if (analyserRef.current) {
        try {
          const tracks =
            audioRef.current?.srcObject instanceof MediaStream
              ? audioRef.current.srcObject.getTracks()
              : [];
          tracks.forEach((track) => track.stop());
        } catch (e) {
          console.error("Error stopping tracks:", e);
        }
      }
    }
  }, [inCall]);

  useEffect(() => {
    if (!audioRef.current || !audioRef.current.srcObject) return;

    const audioContext = new AudioContext();
    const analyser = audioContext.createAnalyser();
    analyser.fftSize = 32; // Small FFT size for basic visualizer
    const bufferLength = analyser.frequencyBinCount;
    const dataArray = new Uint8Array(bufferLength);

    const source = audioContext.createMediaStreamSource(
      audioRef.current.srcObject as MediaStream
    );
    source.connect(analyser);

    analyserRef.current = analyser;
    dataArrayRef.current = dataArray;

    const updateVisualizer = () => {
      if (!analyserRef.current || !dataArrayRef.current) return;

      analyserRef.current.getByteFrequencyData(dataArrayRef.current);
      // Calculate average audio level (0-1)
      const average =
        Array.from(dataArrayRef.current).reduce(
          (sum, value) => sum + value,
          0
        ) /
        (dataArrayRef.current.length * 255);

      setAudioLevel(average);
      animationRef.current = requestAnimationFrame(updateVisualizer);
    };

    animationRef.current = requestAnimationFrame(updateVisualizer);

    return () => {
      if (animationRef.current) {
        cancelAnimationFrame(animationRef.current);
      }
      if (source) {
        source.disconnect();
      }
      audioContext.close();
    };
  }, [audioRef, audioRef.current?.srcObject]);

  // Calculate ring properties based on audio level
  const baseRadius = 60;
  const maxExpansion = 20;
  const ringThickness = 10 + audioLevel * 12; // Thickness increases with volume
  const ringRadius = baseRadius + audioLevel * maxExpansion; // Ring expands with volume

  return (
    <Box
      sx={{
        position: "absolute",
        width: 160,
        height: 160,
        left: "50%",
        top: "50%",
        transform: "translate(-50%, -50%)",
        zIndex: 0,
      }}
    >
      <svg width="160" height="160" viewBox="0 0 160 160">
        <circle
          cx="80"
          cy="80"
          r={ringRadius}
          fill="none"
          stroke="#4CAF50" // Green ring
          strokeWidth={ringThickness}
          strokeOpacity={0.6 + audioLevel * 0.4} // Opacity increases with volume
          style={{
            transition:
              "stroke-width 0.01s ease, r 0.01s ease, stroke-opacity 0.01s ease",
          }}
        />
      </svg>
    </Box>
  );
};

const Chat = () => {
  const [messages, setMessages] = useState<Message[]>([]);
  const [inputValue, setInputValue] = useState("");
  const [socket, setSocket] = useState<ReturnType<typeof io> | null>(null);
  const socketRef = useRef<ReturnType<typeof io> | null>(null);
  //const [inCall, setInCall] = useState(false); // State to track if a call is in progress
  const { inCall, setInCall } = useCallStatus();
  const [audioLevel, setAudioLevel] = useState(0); // State to track audio level
  const [users, setUsers] = useState<User[]>([]); // State to track users in the room
  const dataChannelRef = useRef<RTCDataChannel | null>(null);
  const peerConnectionRef = useRef<RTCPeerConnection | null>(null);
  const localStreamRef = useRef<MediaStream | null>(null);
  const remoteAudioRef = useRef<HTMLAudioElement | null>(null); // Add a ref for the remote audio element
  const messagesEndRef = useRef<HTMLDivElement>(null); // Ref for auto-scrolling
  const pendingCandidates = useRef<RTCIceCandidateInit[]>([]); // Ref for pending ICE candidates
  const isNegotiatingCodec = useRef(false);
  const [filterCriteria, setFilterCriteria] = useState<FilterCriteria>(
    getDefaultFilterCriteria()
  );
  const [isMessageCooldown, setIsMessageCooldown] = useState(false);
  const [charCount, setCharCount] = useState(0);
  const MAX_CHARS = 500;
  const COOLDOWN_TIME = 3000; // 3 seconds in milliseconds
  const [hadPartner, setHadPartner] = useState(false);
  const isRestartingICE = useRef(false);
  const [connectionState, setConnectionState] = useState<
    "idle" | "connecting" | "connected" | "failed"
  >("idle");
  const hasSetConnectedRef = useRef(false);
  const isStateStabilizing = useRef(false);
  const connectionStateTransitionsRef = useRef<
    {
      timestamp: number;
      state: "idle" | "connecting" | "connected" | "failed";
    }[]
  >([]);
  const hasTriggeredCodecTest = useRef(false);
  const currentRoomIdRef = useRef<string | null>(null);
  const [remoteUserProfile, setRemoteUserProfile] = useState({
    userName: "",
    userHandle: "",
    politicalView: "",
  });

  const isLargeScreen = useMediaQuery("(min-width:800px)");
  const navigate = useNavigate(); // Add this hook
  const [error, setError] = useState(""); // Add error state if not already defined

  const { userProfile } = useUserProfile();

  // Create a basic user profile. These values should be stored in user settings within the database
  /* const [userProfile, setUserProfile] = useState({
        ethnicity: "select",
        religion: "select",
        location: "select", 
        politicalView: "select",
        topic: "",
        verified: false,
        veteran: false,
        userName: "New User",
        userHandle: "@newuser"
});

    // Update userProfile when topic changes in filterCriteria
    useEffect(() => {
        setUserProfile(prev => ({
            ...prev,
            topic: filterCriteria.topic
        }));
    }, [filterCriteria.topic]);*/

  const handleFilterChange = (criteria: FilterCriteria) => {
    //console.log("Filter criteria updated:", criteria);
    setFilterCriteria(criteria);
  };

  useEffect(() => {
    // Auto-scroll to bottom when new messages arrive
    if (messagesEndRef.current) {
      messagesEndRef.current.scrollIntoView({ behavior: "smooth" });
    }
  }, [messages]); // Dependency on messages array ensures it runs when messages change

  useEffect(() => {
    if (!inCall) return;

    const connectionMonitor = setInterval(() => {
      if (
        dataChannelRef.current?.readyState === "open" &&
        connectionState !== "connected"
      ) {
        //console.log('Connection monitor: Data channel is open but connection state is not "connected"');
        setConnectionState("connected");
      }
    }, 1000); // Check every second

    return () => clearInterval(connectionMonitor);
  }, [inCall, connectionState]);

  // Cleanup on component unmount
  useEffect(() => {
    return () => {
      handleEndCall();
    };
  }, []);

  useEffect(() => {
    const unlockAudio = async () => {
      try {
        // Create silent audio context
        const audioContext = new AudioContext();

        // Create a silent oscillator
        const oscillator = audioContext.createOscillator();
        const gainNode = audioContext.createGain();
        gainNode.gain.value = 0; // completely silent
        oscillator.connect(gainNode);
        gainNode.connect(audioContext.destination);

        // Oscillator needs to start and stop
        oscillator.start(0);
        oscillator.stop(0.001);

        //console.log('Audio context initialized for later use');

        // Listen for first user interaction with the page
        const unlockOnInteraction = () => {
          if (!navigator.mediaDevices) {
            console.error(
              "MediaDevices API not available - are you using HTTPS?"
            );
            return;
          }

          navigator.mediaDevices
            .getUserMedia({ audio: true })
            .then((stream) => {
              //console.log('Audio permissions granted on first interaction');
              localStreamRef.current = stream;
            })
            .catch((err) =>
              console.error("Could not get audio permissions:", err)
            );

          // Remove event listeners
          ["mousedown", "touchstart", "keydown"].forEach((event) => {
            document.removeEventListener(event, unlockOnInteraction);
          });
        };

        // Add event listeners for first interaction
        ["mousedown", "touchstart", "keydown"].forEach((event) => {
          document.addEventListener(event, unlockOnInteraction);
        });
      } catch (err) {
        console.error("Error initializing audio context:", err);
      }
    };

    unlockAudio();
  }, []);

  // Test local microphone capture
  /*const testLocalAudio = () => {
        navigator.mediaDevices.getUserMedia({ audio: true })
            .then(stream => {
                // Create audio context for visualization
                const audioContext = new AudioContext();
                const analyser = audioContext.createAnalyser();
                const microphone = audioContext.createMediaStreamSource(stream);
                const javascriptNode = audioContext.createScriptProcessor(2048, 1, 1);
                
                // Connect the nodes
                microphone.connect(analyser);
                analyser.connect(javascriptNode);
                javascriptNode.connect(audioContext.destination);
                
                // Set up visualization data
                analyser.fftSize = 256;
                const bufferLength = analyser.frequencyBinCount;
                const dataArray = new Uint8Array(bufferLength);
                
                // Print audio levels to console for 5 seconds
                let testInterval = setInterval(() => {
                    analyser.getByteFrequencyData(dataArray);
                    const average = dataArray.reduce((a, b) => a + b) / bufferLength;
                    //console.log(`Microphone audio level: ${average}`);
                    
                    if (average > 10) {
                        //console.log('✅ Microphone is working - audio detected');
                    } else {
                        //console.log('⚠️ No significant audio detected - try making some noise');
                    }
                }, 500);
                
                // Stop after 5 seconds
                setTimeout(() => {
                    clearInterval(testInterval);
                    stream.getTracks().forEach(track => track.stop());
                    //console.log('Microphone test completed');
                }, 5000);
            })
            .catch(err => {
                console.error('Error accessing microphone:', err);
            });
    };*/

  /*const handleFilterApply = (criteria: any) => {
    //console.log('Applied filters:', criteria);
    setActiveFilters(criteria);
    };*/

  // Fetch TURN credentials
  const fetchTurnCredentials = async () => {
    try {
      //console.log('Fetching TURN credentials from server...');
      //const response = await fetch('http://signalingservice.arcintra.com/turn-credentials'); /*LOCAL TESTING*/
      //const response = await fetch('https://signalingservice.arcintra.com/turn-credentials'); /*DEVBUILD TESTING*/
      //const response = await fetch('http://AA-signaling-env.eba-i4sywtdn.us-east-2.elasticbeanstalk.com/turn-credentials');/* LOCAL PRODUCTION TESTING*/
      const response = await fetch(
        "https://aasignaling.azsyss.com/turn-credentials"
      ); /* PRODUCTION */
      //const response = await fetch('https://ddzqwefksc15g.cloudfront.net/turn-credentials'); /* PRODUCTION */
      const data = await response.json();

      if (
        !data.iceServers ||
        !data.iceServers.username ||
        !data.iceServers.credential
      ) {
        throw new Error("Invalid TURN credential format");
      }

      return {
        iceServers: [
          // Start with STUN servers first
          { urls: ["stun:stun.l.google.com:19302"] },
          { urls: ["stun:stun1.l.google.com:19302"] },

          // Then Cloudflare TURN with UDP first (lower overhead than TCP)
          {
            urls: ["turn:turn.cloudflare.com:3478?transport=udp"],
            username: data.iceServers.username,
            credential: data.iceServers.credential,
          },
          // Only fall back to TCP if UDP fails
          {
            urls: ["turn:turn.cloudflare.com:3478?transport=tcp"],
            username: data.iceServers.username,
            credential: data.iceServers.credential,
          },
        ],
        iceCandidatePoolSize: 5, // Reduced from 10 to 5
        iceTransportPolicy: "relay", // Use 'relay' only when absolutely necessary
      };
    } catch (error) {
      console.error("Error fetching TURN credentials:", error);
      return {
        iceServers: [{ urls: ["stun:stun.l.google.com:19302"] }],
        iceCandidatePoolSize: 5,
      };
    }
  };

  /*          return {
            iceServers: [

                // Cloudflare TURN server (UDP and TCP support)
                {
                    urls: ["turn:turn.cloudflare.com:3478?transport=udp"],
                    username: data.iceServers.username,
                    credential: data.iceServers.credential
                },
                {
                    urls: ["turn:turn.cloudflare.com:3478?transport=tcp"],
                    username: data.iceServers.username,
                    credential: data.iceServers.credential
                },
                // Google STUN server (for basic connectivity)
                { urls: ["stun:stun.l.google.com:19302"] },

                // Twilio TURN server (for fallback)
                {
                    urls: ["turn:global.turn.twilio.com:3478?transport=udp"],
                    username: "your-twilio-username",
                    credential: "your-twilio-credential"
                }
            ],
            iceCandidatePoolSize: 10 // Reduce pool size for faster ICE gathering
        };
    } catch (error) {
        console.error('Error fetching TURN credentials:', error);
        return {
            iceServers: [
                { urls: ["stun:stun.l.google.com:19302"] } // Fallback to Google STUN
            ],
            iceCandidatePoolSize: 10
        };
    }
};*/
  // Update your setupPeerConnection function to use the credentials
  const setupPeerConnection = async () => {
    //console.log('Setting up peer connection with adaptive configuration');

    // Get ICE servers configuration
    const iceConfig = await fetchTurnCredentials();

    // Detect if this is a mobile device
    const isMobile =
      /Android|iPhone|iPad|iPod|webOS|BlackBerry|IEMobile|Opera Mini/i.test(
        navigator.userAgent
      );

    // Detect browser
    const browser = {
      isFirefox: navigator.userAgent.indexOf("Firefox") > -1,
      isChrome:
        navigator.userAgent.indexOf("Chrome") > -1 &&
        navigator.userAgent.indexOf("Edge") === -1,
      isSafari:
        navigator.userAgent.indexOf("Safari") > -1 &&
        navigator.userAgent.indexOf("Chrome") === -1,
      isEdge:
        navigator.userAgent.indexOf("Edg") > -1 ||
        navigator.userAgent.indexOf("Edge") > -1,
      isIE: navigator.userAgent.indexOf("Trident") > -1,
    };

    // Apple devices need special handling
    const isAppleDevice = /iPhone|iPad|iPod|Mac/.test(navigator.userAgent);

    // Create RTCPeerConnection with adaptive settings
    const peerConnection = new RTCPeerConnection({
      ...iceConfig,
      //iceTransportPolicy: isMobile ? 'relay' : 'all',
      iceTransportPolicy: "relay",
      bundlePolicy: "max-bundle",
      rtcpMuxPolicy: "require",
      // Safari and Firefox work better with these defaults
      iceServers:
        browser.isSafari || browser.isFirefox
          ? [...iceConfig.iceServers]
          : iceConfig.iceServers,
      //sdpSemantics: 'unified-plan' // This ensures compatibility across browsers
    });

    // Also track ICE connection state since it often provides earlier feedback
    peerConnection.addEventListener("iceconnectionstatechange", () => {
      //console.log(`ICE connection state: ${peerConnection.iceConnectionState}`);
    });

    // Configure connection timeouts adaptively
    peerConnection.addEventListener("iceconnectionstatechange", () => {
      //console.log(`ICE connection state: ${peerConnection.iceConnectionState}`);

      if (peerConnection.iceConnectionState === "failed") {
        // Implement progressive ICE restart strategy
        if (!isRestartingICE.current) {
          isRestartingICE.current = true;
          console.warn("ICE connection failed, restarting...");

          // First try normal ICE restart
          peerConnection.restartIce();

          // If still failing after 5 seconds, try more aggressive recovery
          setTimeout(() => {
            if (peerConnection.iceConnectionState === "failed") {
              console.warn(
                "ICE restart didn't work, trying more aggressive recovery..."
              );
              recreateOffer(true); // Force new offer with ICE restart
            }
            isRestartingICE.current = false;
          }, 5000);
        }
      }
    });

    // Handle Safari and iOS special cases for audio
    if (isAppleDevice) {
      // iOS Safari needs special audio session handling
      document.addEventListener("touchstart", enableAudioForSafari, {
        once: true,
      });
    }

    // Store reference and return
    peerConnectionRef.current = peerConnection;

    peerConnection.addEventListener("track", (event) => {
      //console.log('Remote track received:', event.track.kind);

      // Create a media stream if it doesn't exist already
      const remoteStream = new MediaStream();
      remoteStream.addTrack(event.track);

      // Connect the stream to the audio element
      if (remoteAudioRef.current) {
        remoteAudioRef.current.srcObject = remoteStream;

        // Try to play immediately and handle autoplay restrictions
        const playPromise = remoteAudioRef.current.play();
        if (playPromise !== undefined) {
          playPromise.catch((error) => {
            console.warn("Autoplay prevented by browser:", error);

            /*// Show user-friendly play button
                        const playButton = document.createElement('button');
                        playButton.innerHTML = '🔊 Click to Enable Audio';
                        playButton.style.position = 'fixed';
                        playButton.style.zIndex = '9999';
                        playButton.style.top = '50%';
                        playButton.style.left = '50%';
                        playButton.style.transform = 'translate(-50%, -50%)';
                        playButton.style.padding = '15px 30px';
                        playButton.style.backgroundColor = '#4CAF50';
                        playButton.style.color = 'white';
                        playButton.style.fontSize = '18px';
                        playButton.style.border = 'none';
                        playButton.style.borderRadius = '5px';
                        playButton.style.cursor = 'pointer';
                        
                        playButton.onclick = () => {
                            remoteAudioRef.current?.play();
                            document.body.removeChild(playButton);
                        };
                        
                        document.body.appendChild(playButton);*/
          });
        }
      } else {
        console.error("No audio element available to receive remote audio");
      }
    });

    peerConnection.onicecandidate = (event) => {
      if (event.candidate) {
        //console.log('Sending ICE candidate to signaling server:', event.candidate.candidate);
        socketRef.current?.emit("ice-candidate", event.candidate);
      } else {
        //console.log('All ICE candidates have been sent');
      }
    };
    peerConnection.onicegatheringstatechange = () => {
      //console.log(`ICE gathering state: ${peerConnection.iceGatheringState}`);

      if (peerConnection.iceGatheringState === "complete") {
        //console.log('ICE gathering complete, sending final SDP');
        socketRef.current?.emit("offer", peerConnection.localDescription);
      }
    };

    return peerConnection;
  };

  const recreateOffer = async (iceRestart: boolean = false) => {
    if (!peerConnectionRef.current || !socketRef.current) return;

    //console.log(`Recreating offer ${iceRestart ? 'with' : 'without'} ICE restart`);

    try {
      // Create new offer with ICE restart if requested
      const offerOptions: RTCOfferOptions = {
        offerToReceiveAudio: true,
        offerToReceiveVideo: false,
        iceRestart: iceRestart,
      };

      const offer = await peerConnectionRef.current.createOffer(offerOptions);

      // Set as local description
      await peerConnectionRef.current.setLocalDescription(offer);
      //console.log('Set local description for recreated offer');

      // Wait briefly to collect some ICE candidates
      await new Promise((resolve) => setTimeout(resolve, 1000));

      // Send the latest offer to the remote peer
      //console.log('Sending recreated offer to signaling server');
      socketRef.current.emit(
        "offer",
        peerConnectionRef.current.localDescription || offer
      );
    } catch (error) {
      console.error("Error recreating offer:", error);
    }
  };

  const setupLocalMedia = async () => {
    try {
      //console.log('Setting up local media with device-specific optimizations');

      // Detect device capabilities
      const hasGetDisplayMedia = !!(
        navigator.mediaDevices && navigator.mediaDevices.getDisplayMedia
      );
      const supportsStereo = !/iPhone|iPad|iPod/.test(navigator.userAgent);
      const isMobile =
        /Android|iPhone|iPad|iPod|webOS|BlackBerry|IEMobile|Opera Mini/i.test(
          navigator.userAgent
        );

      // Get user media with optimized constraints
      const constraints: MediaStreamConstraints = {
        audio: {
          echoCancellation: true,
          noiseSuppression: true,
          autoGainControl: true,
          //channelCount: 1,
          // Add stereo support on supported devices
          ...(supportsStereo && {
            channelCount: 2,
          }),
        },
        video: false,
      };

      // Get the local stream
      const localStream = await navigator.mediaDevices.getUserMedia(
        constraints
      );
      localStreamRef.current = localStream;

      // Add the local stream to the peer connection
      if (peerConnectionRef.current) {
        localStream.getTracks().forEach((track) => {
          peerConnectionRef.current?.addTrack(track, localStream);
        });
      }
      //console.log('Local media setup complete');
      return localStream;
    } catch (error) {
      console.error("Error setting up local media:", error);
      return null;
    }
  };
  // Add this function to your Chat component
  const enableAudioForSafari = async () => {
    try {
      //console.log('Enabling audio for Safari/iOS');

      // Resume audio context if it was suspended
      if (audioContext.state === "suspended") {
        await audioContext.resume();
        //console.log('Audio context resumed for Safari');
      }

      // If we have a remote audio element, try to play it
      if (remoteAudioRef.current) {
        try {
          const playPromise = remoteAudioRef.current.play();
          if (playPromise !== undefined) {
            playPromise.catch((error) => {
              console.warn("Safari auto-play prevented:", error);

              // Create a visible play button for user to interact with
              /*const playButton = document.createElement('button');
            playButton.innerHTML = '🔊 Tap to Enable Audio';
            playButton.style.position = 'fixed';
            playButton.style.zIndex = '9999';
            playButton.style.top = '20px';
            playButton.style.left = '50%';
            playButton.style.transform = 'translateX(-50%)';
            playButton.style.padding = '10px';
            playButton.style.backgroundColor = '#ff6633';
            playButton.style.color = 'white';
            playButton.style.border = 'none';
            playButton.style.borderRadius = '5px';
            
            playButton.onclick = () => {
              remoteAudioRef.current?.play();
              document.body.removeChild(playButton);
            };
            
            document.body.appendChild(playButton);*/
            });
          }
        } catch (e) {
          //console.error('Error trying to play audio in Safari:', e);
        }
      }

      // Ensure any local stream is properly activated
      if (localStreamRef.current) {
        const tracks = localStreamRef.current.getTracks();
        tracks.forEach((track) => {
          if (!track.enabled) track.enabled = true;
        });
      }
    } catch (err) {
      console.error("Error enabling Safari audio:", err);
    }
  };

  const handleCall = async () => {
    if (!validateProfile(userProfile)) {
      // Show error and redirect to profile
      setError("Please complete your profile before joining a call");
      setTimeout(() => {
        navigate("/profile");
      }, 2000);
      return;
    }

    //console.log('handleCall function executed');

    // Reset connection state
    setConnectionState("idle");

    // Don't do anything if already in a call
    if (inCall) {
      //console.log('Already in a call, ignoring call request');
      return;
    }

    try {
      // Clean up any existing call
      handleEndCall();
      // IMPORTANT: Clear users list immediately to prevent chat flickering
      setUsers([]);

      // Get Firebase UID for reporting purposes
      const auth = getAuth();
      const firebaseUid = auth.currentUser?.uid || "anonymous";
      //console.log(`Sending Firebase UID: ${firebaseUid} to server`);
      // Convert filter criteria to the format expected by the server
      const serverFilters = {
        ethnicity: filterCriteria.ethnicity || "Any",
        religion: filterCriteria.religion || "Any",
        location: filterCriteria.location || "Any",
        politicalView: filterCriteria.politicalView || "Any",
        topic: filterCriteria.topic,
        verified: filterCriteria.verified,
        veteran: filterCriteria.veteran,
        firebaseUid: firebaseUid,
      };

      setInCall(true);
      //const newSocket = io('http://signalingservice.arcintra.com'); /*LOCAL TESTING*/
      //const newSocket = io('https://signalingservice.arcintra.com'); /*DEVBUILD TESTING*/
      //const newSocket = io('http://AA-signaling-env.eba-i4sywtdn.us-east-2.elasticbeanstalk.com'); /* LOCAL PRODUCTION TESTING*/
      const newSocket = io("https://aasignaling.azsyss.com"); /* PRODUCTION */
      //const newSocket = io('https://ddzqwefksc15g.cloudfront.net'); /* PRODUCTION */
      setSocket(newSocket);
      socketRef.current = newSocket;

      newSocket.on("connect", async () => {
        //console.log('Connected to signaling server with ID:', newSocket.id);

        // Send join request with profile and filter data
        newSocket.emit("join", {
          name: userProfile.userName,
          profile: {
            ...userProfile,
            topic: filterCriteria.topic,
            firebaseUid: firebaseUid,
          },
          filters: serverFilters,
        });

        //console.log('Join request sent with filters:', serverFilters);

        // Add a system message to display the Room ID
        /*const roomMessage: Message = {
                id: 'server',
                name: '[Server]',
                message: `Joined Room with ID: ${newSocket.id}`
            };
            setMessages((prevMessages) => [...prevMessages, roomMessage]);*/

        //console.log('Join request sent with filters:', serverFilters);

        // Set up peer connection - now awaiting the async function
        await setupPeerConnection();

        // Set up local media
        await setupLocalMedia();

        // Add explicit transceivers
        addExplicitTransceivers();

        // Create data channel
        if (peerConnectionRef.current) {
          const dataChannel =
            peerConnectionRef.current.createDataChannel("chat");
          dataChannelRef.current = dataChannel;

          dataChannel.onopen = () => {
            //console.log('Data channel opened - forcing connected state');
            setConnectionState("connected");

            // Send your profile to the other user
            if (dataChannelRef.current?.readyState === "open") {
              dataChannelRef.current.send(
                JSON.stringify({
                  type: "profile",
                  profile: userProfile,
                })
              );
            }

            // Clear any transitions to avoid race conditions
            connectionStateTransitionsRef.current = [];
            isStateStabilizing.current = false;
            hasSetConnectedRef.current = true;
          };

          dataChannel.onmessage = (event) => {
            //console.log('Received message via data channel:', event.data);
            try {
              const data = JSON.parse(event.data);
              if (data.type === "profile") {
                // Update remote user profile
                setRemoteUserProfile({
                  userName: data.profile.userName || "Anonymous User",
                  userHandle: data.profile.userHandle || "@anonymous",
                  politicalView: data.profile.politicalView || "Undefined",
                });
              } else {
                // Handle normal messages as before
                const message: Message = data;
                setMessages((prevMessages) => [...prevMessages, message]);
              }
            } catch (error) {
              console.error("Error parsing data channel message:", error);
            }
          };
        }
      });

      newSocket.on("offer_sent_confirmation", (status: string) => {
        //console.log('Server confirmed offer was sent:', status);
      });

      newSocket.on("call_ended", () => {
        //console.log('Remote user ended the call');

        // Create a system message for the chat
        /*const systemMessage: Message = {
                    id: 'system', // Special ID for system messages
                    name: 'System', // System as the sender
                    message: '📞 Call ended by other user'
                };
                
                // Add the message to chat
                setMessages(prevMessages => [...prevMessages, systemMessage]);*/

        // End the call locally
        handleEndCall();
      });

      interface RoomUsersData {
        roomId: string;
        users: User[];
      }

      // Then update your event handler with proper type annotations
      newSocket.on(
        "room_users",
        (data: RoomUsersData | User[], roomIdParam?: string) => {
          const roomUsers = Array.isArray(data) ? data : data.users || [];
          const roomId = Array.isArray(data) ? roomIdParam : data.roomId;
          //console.log('Users in room:', roomUsers);
          //console.log("Room ID:", roomId);

          if (roomUsers.length > 1 && newSocket.id) {
            const otherUser = roomUsers.find(
              (user) => user.id !== newSocket.id
            );
            if (otherUser && otherUser.profile) {
              setRemoteUserProfile({
                userName: otherUser.profile.userName || "Anonymous User",
                userHandle: otherUser.profile.userHandle || "@anonymous",
                politicalView: otherUser.profile.politicalView || "Undefined",
              });
            }
          }

          // Only show the room ID message if it's a new room or first connection
          if (roomId && currentRoomIdRef.current !== roomId) {
            currentRoomIdRef.current = roomId;

            const roomMessage: Message = {
              id: "server",
              name: "[Server]",
              message: `You joined Room ID: ${roomId}`,
              sentByMe: false,
            };
            setMessages((prevMessages) => [...prevMessages, roomMessage]);
          }

          // Always update users list
          setUsers(roomUsers);

          // Rest of your existing code...
          if (roomUsers.length > 1 && newSocket.id !== roomUsers[0].id) {
            //console.log('Client joined room with existing user, setting connection state to connecting');
            setConnectionState("connecting");
          }

          if (
            roomUsers.length > 1 &&
            peerConnectionRef.current &&
            roomUsers[0].id === newSocket.id
          ) {
            //console.log('Multiple users detected, initiating call as first user');
            createAndSendOffer();
          }
        }
      );
      newSocket.on("user_joined", (user: User) => {
        //console.log('User joined:', user);

        if (user.profile) {
          setRemoteUserProfile({
            userName: user.profile.userName || "Anonymous User",
            userHandle: user.profile.userHandle || "@anonymous",
            politicalView: user.profile.politicalView || "Undefined",
          });
        }

        setUsers((prevUsers) => {
          const newUsers = [...prevUsers, user];

          // When a second user joins, immediately set connection state to connecting
          if (newUsers.length === 2) {
            //console.log('Second user joined, setting connection state to connecting');
            setConnectionState("connecting");

            // Only run codec test once by checking the ref
            if (newSocket.id !== user.id && !hasTriggeredCodecTest.current) {
              //console.log('I am the original user and a second user just joined - initiating codec negotiation');
              hasTriggeredCodecTest.current = true; // Prevent future executions

              setTimeout(() => {
                //console.log('Running delayed codec test...');
                try {
                  // First check if connection is still active
                  if (
                    peerConnectionRef.current &&
                    peerConnectionRef.current.connectionState !== "failed"
                  ) {
                    forceAudioCodecTest();
                  }
                } catch (err) {
                  console.error("Error in codec negotiation:", err);
                }
              }, 1500);
            }
          }
          return newUsers;
        });
      });
      newSocket.on("user_exit", (user: User) => {
        //console.log('User left:', user);
        setUsers((prevUsers) => {
          // Ensure prevUsers is always an array
          const prevUsersArray = Array.isArray(prevUsers) ? prevUsers : [];
          const wasNotEmpty = prevUsersArray.length > 1;
          const newUsers = prevUsersArray.filter((u) => u.id !== user.id);

          // If we had more than one user and now we don't
          if (wasNotEmpty && newUsers.length <= 1) {
            setHadPartner(true);

            // Add a system message
            const systemMessage: Message = {
              id: "system",
              name: "System",
              message:
                "👤 User left. Start a new call to chat with someone else.",
              sentByMe: false,
            };
            setMessages((prevMessages) => [...prevMessages, systemMessage]);
          }

          return newUsers;
        });
      });
      newSocket.on("message", (message: Message) => {
        //console.log('Received message via socket:', message);
        message.sentByMe = false;
        setMessages((prevMessages) => [...prevMessages, message]);
      });
      newSocket.on("ice-candidate", async (candidate: RTCIceCandidateInit) => {
        //console.log('Received ICE candidate:', candidate.candidate?.substring(0, 30) + '...' || 'end of candidates');

        if (peerConnectionRef.current) {
          try {
            // Wait for the remote description to be set before adding ICE candidates
            if (peerConnectionRef.current.remoteDescription === null) {
              //console.log('Received ICE candidate before remote description, queuing...');
              pendingCandidates.current.push(candidate);
              return;
            }

            // Add the ICE candidate
            await peerConnectionRef.current.addIceCandidate(
              new RTCIceCandidate(candidate)
            );
            //console.log('ICE candidate added successfully');
          } catch (error) {
            console.error("Error adding received ICE candidate:", error);
          }
        } else {
          console.error(
            "Peer connection not established yet, cannot add ICE candidate"
          );
        }
      });

      newSocket.on("offer", async (offer: RTCSessionDescriptionInit) => {
        //console.log('Received offer:', offer);
        setConnectionState("connecting");

        try {
          // Setup peer connection if not already set
          if (!peerConnectionRef.current) {
            //console.log('Setting up peer connection for incoming offer');
            await setupPeerConnection();
          }

          // Get local media before setting remote description
          await setupLocalMedia();

          if (peerConnectionRef.current) {
            //console.log('Setting remote description from offer');
            await peerConnectionRef.current.setRemoteDescription(
              new RTCSessionDescription(offer)
            );
            //console.log('Remote description set successfully');

            // Add any pending ICE candidates
            if (pendingCandidates.current.length > 0) {
              //console.log(`Adding ${pendingCandidates.current.length} pending ICE candidates`);
              for (const candidate of pendingCandidates.current) {
                try {
                  await peerConnectionRef.current.addIceCandidate(
                    new RTCIceCandidate(candidate)
                  );
                } catch (err) {
                  console.error("Error adding pending ICE candidate:", err);
                }
              }
              pendingCandidates.current = [];
            }

            //console.log('Creating answer');
            const answer = await peerConnectionRef.current.createAnswer();
            //console.log('Answer created:', answer);

            //console.log('Setting local description');
            await peerConnectionRef.current.setLocalDescription(answer);

            //console.log('Sending answer to signaling server');
            newSocket.emit(
              "answer",
              peerConnectionRef.current.localDescription || answer
            );
          }
        } catch (error) {
          console.error("Error handling offer:", error);
        }
      });

      newSocket.on("answer", async (answer: RTCSessionDescriptionInit) => {
        //console.log('Received answer');
        if (peerConnectionRef.current) {
          try {
            await peerConnectionRef.current.setRemoteDescription(
              new RTCSessionDescription(answer)
            );
            //console.log('Remote description set from answer');

            // Now add any pending ICE candidates
            if (
              pendingCandidates.current &&
              pendingCandidates.current.length > 0
            ) {
              //console.log(`Adding ${pendingCandidates.current.length} pending ICE candidates`);

              for (const candidate of pendingCandidates.current) {
                try {
                  await peerConnectionRef.current.addIceCandidate(
                    new RTCIceCandidate(candidate)
                  );
                } catch (err) {
                  console.error("Error adding pending candidate:", err);
                }
              }
              pendingCandidates.current = [];
            }
          } catch (error) {
            console.error(
              "Error setting remote description from answer:",
              error
            );
          }
        }
      });

      // Create data channel
      if (peerConnectionRef.current) {
        const dataChannel = peerConnectionRef.current.createDataChannel("chat");
        dataChannelRef.current = dataChannel;

        dataChannel.onopen = () => {
          //console.log('Data channel opened - forcing connected state');

          // Send your profile to the other user
          if (dataChannelRef.current?.readyState === "open") {
            dataChannelRef.current.send(
              JSON.stringify({
                type: "profile",
                profile: userProfile,
              })
            );
          }

          // Direct state update bypasses the normal channels to ensure it takes effect
          setConnectionState("connected");

          // Clear any transitions to avoid race conditions
          connectionStateTransitionsRef.current = [];
          isStateStabilizing.current = false;
          hasSetConnectedRef.current = true;
        };

        dataChannel.onmessage = (event) => {
          //console.log('Received message via data channel:', event.data);
          try {
            const data = JSON.parse(event.data);
            if (data.type === "profile") {
              // Update remote user profile
              setRemoteUserProfile({
                userName: data.profile.userName || "Anonymous User",
                userHandle: data.profile.userHandle || "@anonymous",
                politicalView: data.profile.politicalView || "Undefined",
              });
            } else {
              // Handle normal messages as before
              const message: Message = data;
              setMessages((prevMessages) => [...prevMessages, message]);
            }
          } catch (error) {
            console.error("Error parsing data channel message:", error);
          }
        };
      }
    } catch (error) {
      console.error("Error in handleCall:", error);
      setInCall(false);
    }
  };

  const createAndSendOffer = async () => {
    //console.log('Creating and sending offer to remote peer');
    if (!peerConnectionRef.current || !socketRef.current) return;

    try {
      // Create comprehensive offer options for better compatibility
      const offerOptions: RTCOfferOptions = {
        offerToReceiveAudio: true,
        offerToReceiveVideo: false,
        iceRestart: isRestartingICE.current,
      };

      // Create the offer
      // Send offer immediately and let ICE candidates trickle
      const offer = await peerConnectionRef.current.createOffer(offerOptions);
      await peerConnectionRef.current.setLocalDescription(offer);

      // Send offer immediately without artificial delay
      //console.log('Sending offer to signaling server');
      socketRef.current.emit(
        "offer",
        peerConnectionRef.current.localDescription
      );

      // Set up a safety timeout in case ICE gathering takes too long
      const iceGatheringTimeout = setTimeout(() => {
        if (peerConnectionRef.current?.iceGatheringState !== "complete") {
          //console.log('ICE gathering taking too long, proceeding with available candidates');
        }
      }, 3000);

      // Clear timeout when ICE gathering completes
      peerConnectionRef.current.onicegatheringstatechange = () => {
        if (peerConnectionRef.current?.iceGatheringState === "complete") {
          clearTimeout(iceGatheringTimeout);
        }
      };

      // Send offer to signaling server with the most up-to-date SDP
      //console.log('Sending offer to signaling server');
      socketRef.current.emit(
        "offer",
        peerConnectionRef.current.localDescription || offer
      );
    } catch (error) {
      console.error("Error creating or sending offer:", error);
    }
  };

  const handleEndCall = () => {
    if (!inCall) return;

    setRemoteUserProfile({
      userName: "",
      userHandle: "",
      politicalView: "",
    });

    //console.log('Ending call');

    // Reset room ID tracking
    currentRoomIdRef.current = null;

    // Add message to chat that you ended the call
    const systemMessage: Message = {
      id: "system",
      name: "System",
      message: "📞 You left the call",
      sentByMe: false,
    };
    setMessages((prevMessages) => [...prevMessages, systemMessage]);

    // Reset hadPartner state when user ends call
    setHadPartner(false);

    // Send call end notification BEFORE disconnecting
    if (socket) {
      socket.emit("call_ended");

      // Small delay to ensure the message is sent before disconnecting
      setTimeout(() => {
        // Remove all listeners before disconnecting
        socket.removeAllListeners();
        socket.disconnect();
        setSocket(null);
      }, 100);
    }

    if (localStreamRef.current) {
      localStreamRef.current.getTracks().forEach((track) => {
        track.stop();
        //console.log('Stopped local track:', track.kind);
      });
      localStreamRef.current = null;
    }

    if (peerConnectionRef.current) {
      peerConnectionRef.current.close();
      peerConnectionRef.current = null;
      //console.log('Peer connection closed');
    }

    if (dataChannelRef.current) {
      dataChannelRef.current.close();
      dataChannelRef.current = null;
    }

    if (socketRef.current) {
      socketRef.current.disconnect();
      socketRef.current = null;
      //console.log('Socket disconnected');
    }

    if (remoteAudioRef.current) {
      remoteAudioRef.current.srcObject = null;
    }

    setInCall(false);
    setAudioLevel(0);
    //console.log('Call ended');
    hasTriggeredCodecTest.current = false; // Reset for future calls
  };
  // Update the handleSubmit function
  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    if (inputValue.trim() === "" || isMessageCooldown) return;

    const message: Message = {
      id: socket?.id || "local-user",
      name: "You", // Always display as "You" for your own messages
      message: inputValue,
      sentByMe: true,
    };
    //console.log('Sending message:', message);

    // Create a separate message object to send to the other user with your real name
    const messageToSend = {
      id: socket?.id || "local-user",
      name: userProfile.userName || "Unknown User", // Send real username to others
      message: inputValue,
    };

    // Send the message with your real username to others
    if (
      dataChannelRef.current &&
      dataChannelRef.current.readyState === "open"
    ) {
      try {
        //console.log('Sending via data channel');
        dataChannelRef.current.send(JSON.stringify(messageToSend));
      } catch (error) {
        console.error("Error sending via data channel:", error);
      }
    }

    // Always send via socket as fallback with real username
    if (socket) {
      //console.log('Sending via socket');
      socket.emit("message", messageToSend);
    }

    // Add message to local state with "You" as the name
    setMessages((prevMessages) => [...prevMessages, message]);
    setInputValue("");
    setCharCount(0);

    // Set cooldown
    setIsMessageCooldown(true);
    setTimeout(() => {
      setIsMessageCooldown(false);
    }, COOLDOWN_TIME);
  };

  // Update the input onChange handler
  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const newValue = e.target.value;
    if (newValue.length <= MAX_CHARS) {
      setInputValue(newValue);
      setCharCount(newValue.length);
    }
  };

  // Explicitly add transceivers to connection
  const addExplicitTransceivers = () => {
    if (!peerConnectionRef.current) return;

    try {
      // Check if we already have transceivers first
      const existingTransceivers = peerConnectionRef.current.getTransceivers();
      const hasAudioTransceiver = existingTransceivers.some(
        (t) =>
          t.sender.track?.kind === "audio" ||
          (t.mid !== null && t.receiver.track?.kind === "audio")
      );

      // Only add if we don't already have one
      if (!hasAudioTransceiver) {
        //console.log('No existing audio transceiver, adding one');
        const transceiver = peerConnectionRef.current.addTransceiver("audio", {
          direction: "sendrecv",
          streams: localStreamRef.current
            ? [localStreamRef.current]
            : undefined,
        });
        //console.log('Added explicit audio transceiver:', transceiver);
      } else {
        //console.log('Audio transceiver already exists, skipping creation');
      }
    } catch (e) {
      console.error("Error in addExplicitTransceivers:", e);
    }
  };

  // Force specified audio codec test
  const forceAudioCodecTest = async () => {
    if (!peerConnectionRef.current || isNegotiatingCodec.current) return;

    try {
      //console.log('CODEC TEST: Starting codec-specific test');
      isNegotiatingCodec.current = true;

      // 1. Find the audio transceiver
      const transceivers = peerConnectionRef.current.getTransceivers();
      const audioTransceiver = transceivers.find(
        (t) =>
          t.sender.track?.kind === "audio" ||
          (t.mid !== null && t.receiver.track?.kind === "audio")
      );

      if (!audioTransceiver) {
        console.error("No audio transceiver found for codec negotiation");
        isNegotiatingCodec.current = false;
        return;
      }

      //console.log('Found audio transceiver with mid:', audioTransceiver.mid);

      // 2. Set preferred codecs (OPUS with high quality)
      // Add a null check before accessing codecs
      const capabilities = RTCRtpSender.getCapabilities("audio");
      if (!capabilities) {
        //console.log('CODEC TEST: Audio capabilities not available in this browser');
        isNegotiatingCodec.current = false;
        return;
      }

      const codecs = capabilities.codecs;
      const opusCodec = codecs.find(
        (c) => c.mimeType.toLowerCase() === "audio/opus"
      );

      if (opusCodec) {
        //console.log('Setting preferred codec to OPUS');
        audioTransceiver.setCodecPreferences([opusCodec]);
      }

      // 3. Create new offer with codec preference
      //console.log('Creating new offer with codec preferences');
      const offer = await peerConnectionRef.current.createOffer();

      // 4. Set local description
      await peerConnectionRef.current.setLocalDescription(offer);
      //console.log('Set local description with codec preferences');

      // 5. Send the offer to the remote peer
      //console.log('Sending updated offer to signaling server');
      socketRef.current?.emit(
        "offer",
        peerConnectionRef.current.localDescription
      );

      // Reset the flag after a delay
      setTimeout(() => {
        isNegotiatingCodec.current = false;
      }, 5000);
    } catch (e) {
      console.error("CODEC TEST: Error setting codec preferences:", e);
      isNegotiatingCodec.current = false;
    }
  };
  /* // Test Functions
const testCloudflareConnection = async () => {
  try {
    //console.log('Testing Cloudflare TURN connection...');
    
    // Get credentials
    const turnConfig = await fetchTurnCredentials();
    //console.log('Got TURN credentials:', turnConfig);
    
    // Create test peer connection with only Cloudflare TURN
    const pc = new RTCPeerConnection({
      iceServers: [{
        urls: [
          "turn:turn.cloudflare.com:3478?transport=udp",
          "turn:turn.cloudflare.com:3478?transport=tcp"
        ],
        username: turnConfig.iceServers.username,
        credential: turnConfig.iceServers.credential
      }],
      iceTransportPolicy: 'relay' // Force TURN usage
    });
    
    // Log all candidates
    pc.onicecandidate = e => {
      if (e.candidate) {
        //console.log('ICE candidate:', e.candidate.candidate);
        
        // Check if it's a relay candidate from Cloudflare
        if (e.candidate.candidate.includes('typ relay')) {
          //console.log('✅ SUCCESS: Cloudflare TURN relay candidate found!');
        }
      } else {
        //console.log('ICE gathering complete');
        
        // Check the SDP for relay candidates
        const sdp = pc.localDescription?.sdp || '';
        const relayCount = (sdp.match(/typ relay/g) || []).length;
        
        if (relayCount > 0) {
          //console.log(`✅ Found ${relayCount} relay candidates - Cloudflare TURN works!`);
        } else {
          console.error('❌ No relay candidates found - Cloudflare TURN isn\'t working');
        }
      }
    };
    
    // Create a data channel to trigger ICE gathering
    pc.createDataChannel('turnTest');
    
    // Create and set local description
    const offer = await pc.createOffer();
    await pc.setLocalDescription(offer);
    
  } catch (e) {
    console.error('Cloudflare TURN test error:', e);
  }
};

// NAT traversal diagnostic
    const testNATTraversal = async () => {
        try {
            //console.log('🔍 Starting NAT traversal test...');
            // Create a minimal peer connection just to check connectivity
            const pc = new RTCPeerConnection({
                iceServers: [
                    // Try multiple public STUN/TURN servers
                    { urls: "stun:stun.l.google.com:19302" },
                    { urls: "stun:stun1.l.google.com:19302" },
                    { 
                        urls: 'turn:turn.anyfirewall.com:3478',
                        username: 'webrtc',
                        credential: 'webrtc'
                    },
                    // Global TURN server - this is an emergency service for testing
                    { 
                        urls: 'turn:global.turn.twilio.com:3478?transport=udp',
                        username: 'f4b4035eaa76f77e3423b4d0829ed600f18b44735443cc1161329eae88e8663f',
                        credential: 'khrs2EaOiOj98GE/vXEXAd/Jhtw3T/IyEYMEcFbJOlQ='
                    }
                ],
                iceCandidatePoolSize: 10
            });
            // Log all ICE candidates
            pc.onicecandidate = e => {
                if (e.candidate) {
                    const candidateType = e.candidate.candidate.includes('typ host') ? 'host' :
                                        e.candidate.candidate.includes('typ srflx') ? 'srflx' :
                                        e.candidate.candidate.includes('typ relay') ? 'relay' : 'unknown';
                    
                    //console.log(`ICE candidate found: ${candidateType}`);
                    
                    // If we get a relay candidate, that's very good news
                    if (candidateType === 'relay') {
                        //console.log('✅ TURN relay candidate found - should be able to connect through restrictive NATs');
                    }
                    
                    // If we get a server reflexive candidate, that's good but not guaranteed
                    if (candidateType === 'srflx') {
                        //console.log('✓ STUN server reflexive candidate found - may be able to connect through NATs');
                    }
                }
            };
            // Monitor state changes
            pc.oniceconnectionstatechange = () => {
                //console.log('ICE connection state:', pc.iceConnectionState);
            };
            
            // Create a data channel (needed to generate ICE candidates)
            pc.createDataChannel('test');
            
            // Create offer and set local description
            const offer = await pc.createOffer();
            await pc.setLocalDescription(offer);
            
            // Wait for ICE gathering to complete or timeout after 5 seconds
            await new Promise(resolve => {
                const checkState = () => {
                    if (pc.iceGatheringState === 'complete') {
                        //console.log('✅ ICE gathering completed');
                        resolve(true);
                        return;
                    }
                    
                    setTimeout(checkState, 500);
                };
                
                // Start checking and also set timeout
                checkState();
                setTimeout(() => {
                    if (pc.iceGatheringState !== 'complete') {
                        //console.log('⚠️ ICE gathering timed out - this may indicate network issues');
                        resolve(false);
                    }
                }, 5000);
            });
            
            // Count candidate types to determine NAT traversal capability
            const sdp = pc.localDescription?.sdp || '';
            const hostCount = (sdp.match(/typ host/g) || []).length;
            const srflxCount = (sdp.match(/typ srflx/g) || []).length;
            const relayCount = (sdp.match(/typ relay/g) || []).length;
            
            //console.log('=== NAT Traversal Test Results ===');
            //console.log(`Host candidates: ${hostCount}`);
            //console.log(`Server reflexive candidates: ${srflxCount}`);
            //console.log(`Relay candidates: ${relayCount}`);
            
            // Make diagnosis
            if (relayCount === 0 && srflxCount === 0) {
                console.error('❌ CRITICAL: No STUN or TURN candidates found. Your network likely blocks WebRTC!');
                //console.log('Recommendation: Try connecting from a different network or use a VPN.');
            } else if (relayCount === 0) {
                console.warn('⚠️ No TURN relay candidates found. May not connect through symmetric NATs.');
                //console.log('Recommendation: Try using more TURN servers.');
            } else {
                //console.log('✅ Found all candidate types. NAT traversal should work.');
            }
            
            // Clean up
            pc.close();
            
        } catch (e) {
            console.error('NAT traversal test error:', e);
        }
    }; */

  /*const fixCommonWebRTCIssues = async () => {
            if (!peerConnectionRef.current || !remoteAudioRef.current) return;
            
            //console.log('⚙️ Applying WebRTC recovery procedures...');
            let recoverySuccess = false;
            
            try {
                // 1. Basic Audio Fixes
                if (remoteAudioRef.current) {
                    remoteAudioRef.current.volume = 1.0;
                    
                    // Try to restart audio playback if it was interrupted
                    if (remoteAudioRef.current.paused && remoteAudioRef.current.srcObject) {
                        //console.log('Remote audio was paused, attempting to play');
                        await remoteAudioRef.current.play().catch(err => 
                            console.warn('Could not auto-resume audio playback:', err)
                        );
                    }
                }
                
                // 2. Track Enablement
                const receivers = peerConnectionRef.current.getReceivers();
                const audioReceiver = receivers.find(r => r.track?.kind === 'audio');
                if (audioReceiver && audioReceiver.track) {
                    audioReceiver.track.enabled = true;
                    //console.log('Remote audio track re-enabled');
                }
                
                // 3. Transceiver Direction Reset
                const transceivers = peerConnectionRef.current.getTransceivers();
                const audioTransceiver = transceivers.find(t => 
                    t.receiver.track?.kind === 'audio' || 
                    t.sender.track?.kind === 'audio'
                );
                
                if (audioTransceiver && audioTransceiver.direction !== 'sendrecv') {
                    //console.log(`Fixing transceiver direction from ${audioTransceiver.direction} to sendrecv`);
                    audioTransceiver.direction = 'sendrecv';
                    recoverySuccess = true;
                }
                
                // 4. ICE Connection Recovery
                if (peerConnectionRef.current.iceConnectionState === 'failed' || 
                    peerConnectionRef.current.iceConnectionState === 'disconnected') {
                    
                    //console.log('ICE connection issue detected, attempting restartIce()');
                    peerConnectionRef.current.restartIce();
                    
                    // Wait to see if ICE restart helps
                    await new Promise(resolve => setTimeout(resolve, 2000));
                    
                    // If still in a bad state, try recreating an offer
                    if (peerConnectionRef.current.iceConnectionState === 'failed' || 
                        peerConnectionRef.current.iceConnectionState === 'disconnected') {
                        
                        //console.log('ICE restart didn\'t work, attempting SDP renegotiation');
                        try {
                            await recreateOffer(true); // Force new offer with ICE restart flag
                            recoverySuccess = true;
                        } catch (err) {
                            console.error('Failed to recreate offer:', err);
                        }
                        
                        // Wait for offer/answer exchange to take effect
                        await new Promise(resolve => setTimeout(resolve, 3000));
                    } else {
                        recoverySuccess = true;
                    }
                }
                
                // 5. Last Resort - reconnect media only if we still have a data channel
                if (!recoverySuccess && dataChannelRef.current?.readyState === 'open') {
                    //console.log('Attempting media track recovery while keeping data channel');
                    
                    // Remove and re-add media tracks without disturbing data channel
                    if (localStreamRef.current) {
                        const senders = peerConnectionRef.current.getSenders();
                        senders.forEach(sender => {
                            if (sender.track?.kind === 'audio') {
                                peerConnectionRef.current?.removeTrack(sender);
                            }
                        });
                        
                        // Re-add audio tracks
                        localStreamRef.current.getAudioTracks().forEach(track => {
                            peerConnectionRef.current?.addTrack(track, localStreamRef.current!);
                        });
                        
                        // Trigger renegotiation
                        await recreateOffer(true);
                        recoverySuccess = true;
                    }
                }
                
                // 6. Final verdict - if nothing worked and we're still in a failed state after trying everything
                if (!recoverySuccess && 
                    (peerConnectionRef.current.connectionState === 'failed' || 
                    peerConnectionRef.current.iceConnectionState === 'failed')) {
                    
                    console.error('All WebRTC recovery attempts failed');
                    
                    // Add a system message about the failed connection
                    const systemMessage = {
                        id: 'system',
                        name: 'System',
                        message: '❌ Call quality issues detected. The audio connection was lost.',
                        sentByMe: false 
                    };
                    setMessages(prev => [...prev, systemMessage]);
                    
                    // If we have a data channel still working, keep it open
                    if (dataChannelRef.current?.readyState === 'open') {
                        //console.log('Data channel still working - keeping connection for text chat only');
                        
                        // Add another message that we can still use text chat
                        const chatMessage = {
                            id: 'system',
                            name: 'System',
                            message: '💬 Text chat is still available.',
                            sentByMe: false 
                        };
                        setMessages(prev => [...prev, chatMessage]);
                    } else {
                        // Only end the call if absolutely everything failed including data channel
                        //console.log('Connection completely failed - ending call');
                        handleEndCall();
                    }
                }
            } catch (err) {
                console.error('Error during WebRTC recovery process:', err);
            }
        };*/

  // Test audio output with a simple tone
  /*const testSpeakers = () => {
        try {
            const audioCtx = new AudioContext();
            const oscillator = audioCtx.createOscillator();
            const gainNode = audioCtx.createGain();
            
            oscillator.type = 'sine';
            oscillator.frequency.setValueAtTime(440, audioCtx.currentTime); // A4 note
            gainNode.gain.setValueAtTime(0.5, audioCtx.currentTime); // 50% volume
            
            oscillator.connect(gainNode);
            gainNode.connect(audioCtx.destination);
            
            oscillator.start();
            //console.log('Playing test tone - you should hear a sound');
            
            // Stop after 2 seconds
            setTimeout(() => {
                oscillator.stop();
                //console.log('Test tone stopped');
            }, 2000);
        } catch (e) {
            console.error('Audio output test failed:', e);
        }
    };
    // Test WebRTC connection status
    const checkRTCConnection = () => {
        if (!peerConnectionRef.current) {
            //console.log('❌ No peer connection established');
            return;
        }
        
        //console.log('=== WebRTC Connection Status ===');
        //console.log(`Connection State: ${peerConnectionRef.current.connectionState}`);
        //console.log(`ICE Connection State: ${peerConnectionRef.current.iceConnectionState}`);
        //console.log(`ICE Gathering State: ${peerConnectionRef.current.iceGatheringState}`);
        //console.log(`Signaling State: ${peerConnectionRef.current.signalingState}`);
        
        // Log remote tracks if any
        const receivers = peerConnectionRef.current.getReceivers();
        //console.log(`Remote Tracks: ${receivers.length}`);
        receivers.forEach((receiver, idx) => {
            //console.log(`Track ${idx}: ${receiver.track?.kind}, muted: ${receiver.track?.muted}, enabled: ${receiver.track?.enabled}`);
        });
        
        // Check if we have remote audio
        if (remoteAudioRef.current?.srcObject) {
            const remoteStream = remoteAudioRef.current.srcObject as MediaStream;
            //console.log(`Remote Stream: ${remoteStream.id}`);
            //console.log(`Remote Stream Tracks: ${remoteStream.getTracks().length}`);
            remoteStream.getTracks().forEach((track, idx) => {
                //console.log(`Remote Track ${idx}: ${track.kind}, muted: ${track.muted}, enabled: ${track.enabled}`);
            });
        } else {
            //console.log('❌ No remote stream connected to audio element');
        }
    };*/

  // Verify the peer connection state before creating data channel
  if (peerConnectionRef.current) {
    //console.log('Peer connection ready for data channel:');
    //console.log('- signaling state:', peerConnectionRef.current.signalingState);
    //console.log('- connection state:', peerConnectionRef.current.connectionState);
    //console.log('- ice connection state:', peerConnectionRef.current.iceConnectionState);
    //console.log('- remote tracks:', peerConnectionRef.current.getReceivers().length);
  }

  // Add a new ref for the message container
  const messageContainerRef = useRef<HTMLDivElement>(null);

  // Update the useEffect for auto-scrolling
  useEffect(() => {
    // Auto-scroll to bottom when new messages arrive
    if (messageContainerRef.current) {
      const { scrollHeight, clientHeight } = messageContainerRef.current;
      messageContainerRef.current.scrollTop = scrollHeight - clientHeight;
    }
  }, [messages]); // Dependency on messages array ensures it runs when messages change

  return (
    <Container disableGutters sx={{ width: "100%", mt: 4 }}>
      {/* Error Box */}
      {error && (
        <Box
          sx={{
            backgroundColor: "error.light",
            color: "error.contrastText",
            p: 2,
            mb: 4,
            //borderRadius: 1
          }}
        >
          {error}
        </Box>
      )}
      {/* Profile Section */}
      <Box
        sx={{
          width: "100%",
          height: "7rem",
          backgroundColor: "#002a5a",
          position: "relative",
          display: "flex",
          margin: "auto",
        }}
      >
        {/* Profile Text */}
        <Box
          sx={{
            height: "100%",
            display: "flex",
            mt: "-.3rem",
            alignItems: "center",
            pl: 2,
          }}
        >
          <Box
            sx={{
              color: "white",
              //mt: '1rem',
              //mt: '-.6rem',
              //ml: '1rem',
              margin: "auto",
              textAlign: "left",
              justifyContent: "left",
              alignItems: "left",
              display: "flex",
              flexDirection: "column",
            }}
          >
            {/*{inCall && users.length > 1 && (
                            <Typography 
                                variant="caption" 
                                sx={{ 
                                    backgroundColor: 'rgba(255,255,255,0.7)',
                                    color: '#002a5a',
                                    px: 1,
                                    py: 0.5,
                                    borderRadius: 1,
                                    mt: 0.5,
                                    display: 'inline-block'
                                }}
                            >
                                You're connected with:
                            </Typography>
                        )}*/}
            <Box
              sx={{
                color: "white",
                display: "flex", // Add this
                flexDirection: "column", // Add this
                justifyContent: "center", // Add this
                textAlign: "left",
              }}
            >
              <Typography
                variant="h6"
                sx={{
                  fontWeight: 800,
                  fontSize: {
                    xs: "1.1rem", // Smaller on mobile
                    sm: "1.7rem", // Medium on tablets
                    md: "2.2rem", // Original size on larger screens
                  },
                  lineHeight: {
                    xs: 1.1, // Tighter line height for mobile
                    sm: 1.2, // Slightly more space on tablets
                    md: 1.1, // Tight line height even on larger screens
                  },
                  pl: 0,
                  maxWidth: {
                    xs: "200px",
                    sm: "300px",
                    md: "350px",
                  },
                  overflow: "hidden",
                  textOverflow: "ellipsis",
                  whiteSpace: "normal", // 'wrap' isn't a valid value, using 'normal' instead
                  pt: ".5rem",
                }}
              >
                {inCall && users.length > 1
                  ? remoteUserProfile.userName || "Undefined"
                  : !inCall
                  ? "Candidate"
                  : "Processing..."}
              </Typography>
            </Box>
            <Typography
              sx={{
                ml: ".15rem",
                fontSize: ".9rem",
                pl: 0,
                //mt: '-.45rem'
              }}
            >
              {/*inCall && users.length > 1
                                ? remoteUserProfile.userHandle || "@Undefined"
                                : !inCall 
                                    ? "@[Candidate]" 
                                    : "@Processing..."*/}
            </Typography>
          </Box>
        </Box>

        {/* Political View */}
        {inCall && users.length > 1 && remoteUserProfile.politicalView ? (
          <Box
            sx={{
              height: "70%",
              ml: "auto",
              mr: "1rem",
              display: "flex",
              alignItems: "center",
              justifyContent: "center",

              fontSize: "2rem",
              alignSelf: "center",
            }}
          >
            <Typography
              variant="h6"
              sx={{
                color: "white",
                px: 3,
                py: 1,
                borderRadius: 2,
                fontWeight: "bold",
                fontSize: {
                  xs: "1.1rem", // Smaller on mobile
                  sm: "1.7rem", // Medium on tablets
                  md: "2.2rem", // Original size on larger screens
                },
              }}
            >
              {remoteUserProfile.politicalView}
            </Typography>
          </Box>
        ) : (
          <Box
            component="img"
            src="/American Flag.svg"
            alt="US Flag"
            sx={{
              height: "45%",
              ml: "auto",
              mr: "1rem",
              objectFit: "contain",
              alignSelf: "center",
            }}
          />
        )}
        {/* Avatar */}
        {isLargeScreen && (
          <>
            <AudioVisualizer audioRef={remoteAudioRef} inCall={inCall} />
            <Avatar
              sx={{
                width: 120,
                height: 120,
                position: "absolute",
                left: "50%",
                top: "50%",
                transform: "translate(-50%, -50%)",
                border: "7px solid #002a5a",
                backgroundColor: "#002a5a",
                zIndex: 1, // Ensure avatar is on top of visualizer
              }}
              alt="Profile Picture"
              src="/Star.svg"
            />
          </>
        )}
      </Box>

      {/*<UserProfileSettings 
            userProfile={userProfile}
            setUserProfile={setUserProfile}
            />*/}

      {/* Chat Log Section */}
      <Box sx={{ display: "flex", flexDirection: "column", gap: 1, mt: 4 }}>
        <Paper
          elevation={elevationNum}
          square
          sx={{
            backgroundColor: "#fff",
            display: "flex",
            flexDirection: "column",
            justifyContent: "space-between",
            width: "100%",
            mx: "auto",
            height: "52vh",
            minHeight: "20rem",
            borderRadius: 2,
            border: "1px solid #ddd",
          }}
        >
          {/* Chat */}
          <Box
            ref={messageContainerRef}
            sx={{
              flex: 1,
              overflowY: "auto",
              overflowX: "hidden",
              p: 2,
            }}
          >
            {messages.map((message, index) => (
              <Box
                key={index}
                sx={{
                  textAlign: message.sentByMe ? "right" : "left",
                  mb: 1,
                  display: "flex",
                  flexDirection: "column",
                  alignItems: message.sentByMe ? "flex-end" : "flex-start",
                  maxWidth: "100%",
                }}
              >
                <Typography
                  variant="caption"
                  sx={{
                    fontWeight: "bold",
                    color: message.sentByMe ? "green" : "blue",
                  }}
                >
                  {message.name}
                </Typography>
                <Typography
                  sx={{
                    backgroundColor: message.sentByMe ? "#e6ffe6" : "#e6f2ff",
                    borderRadius: 1,
                    px: 1.5,
                    py: 0.7,
                    maxWidth: "80%",
                    wordBreak: "break-word",
                    whiteSpace: "pre-wrap",
                    display: "inline-block",
                    hyphens: "auto",
                    overflowWrap: "break-word",
                  }}
                >
                  {message.message}
                </Typography>
              </Box>
            ))}
          </Box>
          <Box
            component="form"
            onSubmit={handleSubmit}
            sx={{
              display: "flex",
              flexDirection: "column",
              width: "100%",
              borderTop: "1px solid #eee",
              pt: 0,
            }}
          >
            <Typography
              sx={{
                display: "flex",
                alignItems: "center",
                gap: 1,
                color: "black",
                fontSize: "0.8rem",
                fontWeight: 700,
                px: 2,
              }}
            >
              <ChatBubbleIcon
                sx={{ fontSize: "1.5rem", color: "black", p: 1 }}
              />
              Use Chat to share links or converse!
            </Typography>

            {/* Input container with character counter */}
            <Box
              sx={{
                position: "relative",
                width: "100%",
                px: 2,
                pb: 1,
                boxSizing: "border-box",
              }}
            >
              <Box
                component="input"
                value={inputValue}
                onChange={handleInputChange}
                placeholder={
                  hadPartner
                    ? "Other user left. Start a new call to chat..."
                    : !inCall
                    ? "Start a new call to chat..."
                    : users.length < 2
                    ? "Looking for candidate..."
                    : connectionState === "connecting"
                    ? "Connecting..."
                    : //: connectionState === 'failed'
                    //? "Couldn't connect. Try again..."
                    isMessageCooldown
                    ? "Wait 3s before sending again..."
                    : "Chat..."
                }
                type="text"
                // Update the disabled condition to consider connection state
                disabled={
                  isMessageCooldown ||
                  users.length < 2 ||
                  !inCall ||
                  connectionState === "connecting"
                }
                //|| connectionState === 'failed'}

                sx={{
                  width: "100%",
                  p: 1.5,
                  border: ".5px solid #ddd",
                  borderRadius: 2,
                  outline: "none",
                  boxSizing: "border-box",
                  "&:focus": {
                    borderColor: "#b326ff",
                  },
                  "&:disabled": {
                    backgroundColor: "#f5f5f5",
                    cursor: "not-allowed",
                  },
                }}
              />

              {/* Character counter */}
              <Typography
                variant="caption"
                sx={{
                  position: "absolute",
                  bottom: "16px",
                  right: "24px",
                  backgroundColor: "rgba(255,255,255,0.8)",
                  px: 0.5,
                  borderRadius: 1,
                  color:
                    charCount > MAX_CHARS * 0.8
                      ? charCount > MAX_CHARS * 0.9
                        ? "red"
                        : "orange"
                      : "gray",
                  pointerEvents: "none",
                }}
              >
                {charCount}/{MAX_CHARS}
              </Typography>
            </Box>
          </Box>
        </Paper>
        {/* Call Section */}
        {/* Call Section */}
        <Paper
          elevation={elevationNum}
          sx={{
            backgroundColor: "white",
            pt: 1,
            pb: 0,
            display: "flex",
            flexDirection: "column",
            justifyContent: "space-between",
            borderRadius: 2,
            mx: "auto",
            height: "100%",
            width: "100%",
            border: "1px solid #ddd",
          }}
        >
          <SessionFilter onFilterChange={handleFilterChange} inCall={inCall} />

          {/* Call control icons */}
          <Box
            sx={{
              position: "relative",
              display: "flex",
              justifyContent: "center",
              alignItems: "center",
              gap: 1,
              mt: ".5rem",
              mb: ".5rem",
            }}
          >
            <IconButton
              onClick={handleCall}
              disabled={inCall}
              sx={{
                backgroundColor: inCall ? "gray" : "green",
                color: "white",
                "&:hover": { backgroundColor: inCall ? "gray" : "darkgreen" },
                "&.Mui-disabled": { backgroundColor: "gray", color: "#e0e0e0" },
              }}
            >
              <CallIcon />
            </IconButton>

            <IconButton
              onClick={handleEndCall}
              disabled={!inCall}
              sx={{
                backgroundColor: !inCall ? "gray" : "red",
                color: "white",
                "&:hover": { backgroundColor: !inCall ? "gray" : "darkred" },
                "&.Mui-disabled": { backgroundColor: "gray", color: "#e0e0e0" },
              }}
            >
              <CallEndIcon />
            </IconButton>

            {/* Volume/Mic controls, pinned to the right */}
            <Box
              sx={{
                position: "absolute",
                right: "1rem",
                top: "50%",
                transform: "translateY(-50%)",
              }}
            >
              <VolumeControl
                remoteAudioRef={remoteAudioRef}
                localStreamRef={localStreamRef}
                peerConnectionRef={peerConnectionRef}
                inCall={inCall}
              />
            </Box>
          </Box>
        </Paper>
      </Box>
      {/* Audio element for remote audio */}
      <audio
        ref={remoteAudioRef}
        autoPlay
        style={{ display: "block", marginTop: "10px", width: "100%" }}
      />

      {/* Audio Testing Panel */}
      {/*<Paper 
                elevation={1}
                sx={{
                    mt: 2,
                    p: 2,
                    backgroundColor: '#f8f8f8',
                    
                }}
            >
                <Typography variant="h6" sx={{ mb: 1 }}>Audio Troubleshooting</Typography>
                
                <Box sx={{ display: 'flex', gap: 2, mb: 2, flexWrap: 'wrap' }}>
                    <Button 
                        variant="contained" 
                        color="primary" 
                        onClick={testLocalAudio}
                    >
                        Test Microphone
                    </Button>
                    
                    <Button 
                        variant="contained" 
                        color="secondary" 
                        onClick={testSpeakers}
                    >
                        Test Speakers
                    </Button>
                    
                    <Button 
                        variant="contained" 
                        color="info" 
                        onClick={checkRTCConnection}
                    >
                        Check Connection
                    </Button>
                    
                </Box>*/}

      {/* Audio Volume Controls */}
      {/*<Box sx={{ mb: 2 }}>
                    <Typography variant="subtitle2">Audio Volume</Typography>
                    <input 
                        type="range" 
                        min="0" 
                        max="1" 
                        step="0.1" 
                        defaultValue="1"
                        style={{ width: '100%' }}
                        onChange={(e) => {
                            if (remoteAudioRef.current) {
                                remoteAudioRef.current.volume = parseFloat(e.target.value);
                            }
                        }} 
                    />
                </Box>*/}

      {/* Connection Status */}
      {/*<Box sx={{ mb: 2 }}>
                    <Typography variant="subtitle2">Connection Status</Typography>
                    <Typography variant="body2">
                        Call Active: <span style={{ fontWeight: 'bold', color: inCall ? 'green' : 'red' }}>{inCall ? 'Yes' : 'No'}</span>
                    </Typography>
                    <Typography variant="body2">
                        Users in Room: <span style={{ fontWeight: 'bold' }}>{users.length}</span>
                    </Typography>
                    <Typography variant="body2">
                        Audio Tracks: <span style={{ fontWeight: 'bold' }}>
                            {remoteAudioRef.current?.srcObject ? 
                                ((remoteAudioRef.current.srcObject as MediaStream).getTracks().length || '0') : 
                                '0'}
                        </span>
                    </Typography>
                </Box>
            </Paper>*/}
      {/* Debug Tools */}
      {/*<Paper 
                elevation={1}
                sx={{
                    mt: 2,
                    p: 2,
                    backgroundColor: '#f0f0f0',
                }}
            >
                <Typography variant="h6" sx={{ mb: 1 }}>Advanced Debugging</Typography>
                

                <Button 
                    variant="contained" 
                    color="error"
                    onClick={fixCommonWebRTCIssues}
                    sx={{ ml: 2 }}
                >
                    Fix Audio Issues
                </Button>
                <Button
                    variant="contained"
                    color="primary"
                    onClick={() => {
                        if (peerConnectionRef.current) {
                            // Get SDP for analysis
                            const sdp = peerConnectionRef.current.localDescription?.sdp;
                            if (sdp) {
                                //console.log('=== SDP ANALYSIS ===');
                                //console.log('Contains audio section:', sdp.includes('m=audio') ? 'YES' : 'NO');
                                //console.log('Contains sendrecv:', sdp.includes('a=sendrecv') ? 'YES' : 'NO');
                                //console.log('Contains recvonly:', sdp.includes('a=recvonly') ? 'YES' : 'NO');
                                //console.log('Contains sendonly:', sdp.includes('a=sendonly') ? 'YES' : 'NO');
                                
                                // Count ICE candidates
                                const matches = sdp.match(/candidate/g);
                                //console.log('ICE candidate count:', matches ? matches.length : 0);
                            }
                            
                            // Log all transceivers
                            const transceivers = peerConnectionRef.current.getTransceivers();
                            //console.log('Transceivers:', transceivers.length);
                            transceivers.forEach((t, i) => {
                                //console.log(`Transceiver ${i}:`);
                                //console.log(`- Direction: ${t.direction}`);
                                //console.log(`- Mid: ${t.mid}`);
                                //console.log(`- Sender track: ${t.sender.track ? t.sender.track.kind : 'none'}`);
                                //console.log(`- Receiver track: ${t.receiver.track ? t.receiver.track.kind : 'none'}`);
                            });
                        }
                    }}
                    sx={{ mt: 2 }}
                >
                    Analyze Connection
                </Button>
                <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 2, mt: 2 }}>
                    
                    <Button
                        variant="contained"
                        color="secondary"
                        onClick={forceAudioCodecTest}
                    >
                        Force Audio Codec
                    </Button>
                    <Button
                        variant="contained"
                        color="info"
                        onClick={testNATTraversal}
                    >
                        Test NAT Traversal
                    </Button>
                    <Button
                        variant="contained"
                        color="primary"
                        onClick={testCloudflareConnection}
                        sx={{ mt: 2 }}
                    >
                        Test Cloudflare TURN
                    </Button>
                </Box>
            </Paper>*/}
    </Container>
  );
};
export default Chat;
